import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";
import { createPortal } from "react-dom";
import { useFloatingSlot } from "@/components/floating-slot-context";
import { useOptInPromptShowing } from "@/components/opt-in-prompt/opt-in-prompt-context";
import type { CampaignSchema } from "@/types";
import { cn } from "@/lib/utils";
import { canJoinCampaign } from "@/campaigns/can-join";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAction } from "@/hooks/use-action";
import { useTranslation } from "react-i18next";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";

/**
 * Gradient "Join now!" pill (Figma 5178:644). Uses the shared CTA gradient
 * (solid `--primary` fallback for browsers without oklch relative colours) and
 * the same opt-in action as the campaign card.
 */
export const JoinNowButton = ({
  campaign,
  size = "hero",
  className,
}: {
  campaign: CampaignSchema;
  /** `hero`: the page's one big CTA (44px, set wide rather than tall). `compact`: the 34px pill
   *  that floats over the content. */
  size?: "hero" | "compact";
  className?: string;
}) => {
  const { t } = useTranslation();
  const { optIn } = useAction();

  return (
    // The shared Button in its primary glass.
    <Button
      onClick={() => optIn(campaign)}
      className={cn(
        size === "hero"
          ? "h-11 px-8 text-[15px] font-bold"
          : "h-[34px] px-4 text-[12px] font-bold",
        className
      )}
    >
      {t("campaign.optIn")}
      <ArrowRight
        className={cn("shrink-0", size === "hero" ? "size-[18px]" : "size-3.5")}
        strokeWidth={2.5}
      />
    </Button>
  );
};

/**
 * Where the player stands on joining, shown inline (the overlay's dock panels): a "Joined" pill
 * once they are in — a campaign without opt-in counts every player in — the Opt In button while
 * they still can, nothing on a finished campaign they never joined.
 */
export const CampaignJoinState = ({
  campaign,
  joined,
}: {
  campaign: CampaignSchema;
  joined: boolean;
}) => {
  const { t } = useTranslation();
  if (joined) {
    return (
      <Badge variant="tonal" color="active" className="self-start">
        {t("campaign.joined", "Joined")}
      </Badge>
    );
  }
  if (!canJoinCampaign(campaign, joined)) return null;
  return <JoinNowButton campaign={campaign} className="w-full" />;
};

/**
 * Desktop sidebar join CTA: the campaign's name and one Opt In, on the shared Card in
 * its `primary` glass — the brand colour lit from the top edge — so the invitation is the
 * brightest card in the column without a second kind of surface or a pulsing glow behind it.
 */
export const CampaignJoinCta = ({
  campaign,
  className,
}: {
  campaign: CampaignSchema;
  className?: string;
}) => {
  const { t } = useTranslation();
  // The opt-in prompt is asking about this campaign: one ask at a time.
  if (useOptInPromptShowing(campaign)) return null;
  return (
    // Centred and given air above and below: a small invitation set on its own, not a form row.
    <Card
      primary
      className={cn(
        "flex flex-col items-center gap-4 py-6 text-center",
        className
      )}
    >
      <p className="font-heading text-[15px] leading-snug font-semibold text-foreground">
        {campaignDisplayTitle(campaign, t)}
      </p>
      {/* Hugs its label with roomy side padding — not stretched across the card. */}
      <JoinNowButton campaign={campaign} className="px-10" />
    </Card>
  );
};

/**
 * Mobile sticky join CTA — the full-width "Join now!" button pinned to the bottom of the
 * campaign view, FLOATING over the content: no bar behind it, just a soft fade so cards scroll
 * away underneath and a lift shadow on the button itself. (It used to sit on a solid tinted
 * band, which read as a second toolbar stacked on the in-game campaign dock.) Spans the campaign
 * layout's padding via negative margins so the fade reaches the widget edges; the wrapper lets
 * taps through to the content, only the button catches them.
 */
export const CampaignJoinStickyBar = ({
  campaign,
}: {
  campaign: CampaignSchema;
}) => {
  const slot = useFloatingSlot();
  // The opt-in prompt is asking about this campaign (and pins the page): no pill, no spacer.
  const prompting = useOptInPromptShowing(campaign);
  // The pill is pinned to the PANEL (the layout's floating slot, just above the in-game sheet),
  // not stuck inside the scroll column: `sticky` only ever shifts an element up within its
  // column, so on a short page it would float wherever the content happened to end. What stays
  // in the column is a spacer the pill's height, so the last card can scroll clear of it.
  const pill = (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="cta-float-fade flex justify-center px-4 pt-6 pb-3 ls:justify-end"
    >
      <JoinNowButton
        campaign={campaign}
        size="compact"
        className="cta-float-shadow pointer-events-auto min-w-40 px-6"
      />
    </motion.div>
  );
  if (prompting) return null;
  return (
    <>
      <div aria-hidden="true" className="h-[70px] w-full shrink-0" />
      {slot ? createPortal(pill, slot) : null}
    </>
  );
};
